import { ApexOptions } from 'apexcharts'

// Light theme only. Colours mirror the tokens in DESIGN.md / tailwind.config.js (hex, html2canvas-safe).
export const CHART_COLORS = ['#0042E6', '#007F5E', '#E0701A', '#7C3AED', '#C13E3E', '#0E7490', '#B7791F', '#806D63']
export const CHART_MUTED = '#B5CAFF'
export const CHART_FONT = 'Inter, "Noto Sans TC", "PingFang TC", "Microsoft JhengHei", system-ui, sans-serif'

export const INK = '#23201D'
export const INK_MUTED = '#5E564E'
export const INK_FAINT = '#786E64'
export const SURFACE = '#FFFFFF'
const LINE_SOFT = '#F3F2F0'

export const formatAxisNumber = (value: number) =>
    typeof value === 'number' && Number.isFinite(value) ? Math.round(value).toLocaleString('zh-HK') : ''

export const baseChartOptions: ApexOptions = {
    chart: {
        fontFamily: CHART_FONT,
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: INK_FAINT,
        animations: { speed: 300 },
        background: 'transparent',
        parentHeightOffset: 0,
    },
    theme: { mode: 'light' },
    colors: CHART_COLORS,
    grid: {
        borderColor: LINE_SOFT,
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 8, right: 8 },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2.5 },
    fill: { type: 'solid', opacity: 1 },
    plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '55%' } },
    xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '12px', colors: INK_FAINT }, rotate: 0, hideOverlappingLabels: true, trim: true },
        title: { style: { fontSize: '12px', fontWeight: 500, color: INK_FAINT } },
        tooltip: { enabled: false },
    },
    yaxis: {
        labels: { style: { fontSize: '12px', colors: INK_FAINT }, formatter: formatAxisNumber },
        title: { style: { fontSize: '12px', fontWeight: 500, color: INK_FAINT } },
    },
    legend: {
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        fontWeight: 500,
        labels: { colors: INK_MUTED },
        markers: { size: 6, shape: 'circle' },
        itemMargin: { horizontal: 12 },
    },
    tooltip: { theme: 'light', style: { fontSize: '13px', fontFamily: CHART_FONT }, marker: { show: true } },
    states: { hover: { filter: { type: 'darken' } } },
    noData: { text: '此期間沒有運動紀錄', style: { color: INK_FAINT, fontSize: '14px', fontFamily: CHART_FONT } },
}

type PlainObject = Record<string, unknown>

const isPlainObject = (value: unknown): value is PlainObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value) && typeof value !== 'function'

/** Deep merge (arrays and functions are replaced, not merged). */
export const mergeOptions = (...sources: ApexOptions[]): ApexOptions => {
    const merge = (target: PlainObject, source: PlainObject): PlainObject => {
        const out: PlainObject = { ...target }
        Object.keys(source).forEach((key) => {
            const s = source[key]
            const t = out[key]
            out[key] = isPlainObject(s) && isPlainObject(t) ? merge(t, s) : s
        })
        return out
    }
    return sources.reduce<PlainObject>((acc, src) => merge(acc, src as PlainObject), {}) as ApexOptions
}
